import { useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { easeOut, easeInOut } from "../animations/welcomeAnimations";
import WalkthroughScreen from "./WalkthroughScreen";
import WelcomeScreen from "./WelcomeScreen";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { theme } from "../styles/theme";
import * as styles from "../styles/WelcomeWalkthrough.module.scss";
import { Constants } from "../Constants";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCreative } from "swiper/modules";
import { Swiper as SwiperType } from "swiper/types";

interface WelcomeWalkthroughProps {
  value: {
    showWelcome: boolean;
    handleCompleteWelcome: () => void;
  };
}

const WelcomeWalkthrough: React.FC<WelcomeWalkthroughProps> = ({ value }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { t, i18n } = useTranslation();
  const screens = Constants.welcomeScreens;
  const isWelcomeScreen = screens[activeStep]?.key === "welcome";
  const isLanguageScreen = screens[activeStep]?.key === "language";
  const swiperRef = useRef<SwiperType | null>(null);
  const swiperOffset = 2;

  const handleNext = () => {
    if (activeStep <= 1) {
      setActiveStep((prev) => prev + 1);
    } else if (activeStep > 1 && activeStep < screens.length - 1) {
      if (swiperRef.current) {
        swiperRef.current.slideNext();
      }
    } else {
      value.handleCompleteWelcome();
    }
  };

  const translatedScreens = useMemo(() => {
    return screens.map((screen) => {
      const titleKey = `welcome_screen_${screen.value}_title`;
      const textKey = `welcome_screen_${screen.value}_text`;

      return {
        ...screen,
        ...(i18n.exists(titleKey) && { title: t(titleKey) }),
        ...(i18n.exists(textKey) && { text: t(textKey) }),
      };
    });
  }, [screens, t, i18n]);

  const getButtonText = (index: number) =>
    index === screens.length - 1
      ? t(Constants.translationKeys.get_started)
      : t(Constants.translationKeys.next);

  return (
    <motion.div
      variants={easeInOut}
      key="welcome"
      initial="initial"
      animate="animate"
      exit="exit"
      className={styles.animationContainer}
    >
      <Box
        sx={{ backgroundColor: theme.palette.background.default }}
        className={styles.WelcomeWalkthrough}
      >
        <AnimatePresence mode="wait">
          {isWelcomeScreen && (
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={easeOut}
              key={`screen-${activeStep}`}
            >
              <WelcomeScreen
                title={translatedScreens[0].title}
                text={translatedScreens[0].text}
                handleNextStep={handleNext}
                buttonText={getButtonText(activeStep)}
              />
            </motion.div>
          )}

          {isLanguageScreen && (
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={easeOut}
              key={`screen-${activeStep}`}
            >
              <WalkthroughScreen
                variant="language"
                skipText={t(Constants.translationKeys.skip)}
                handleNextStep={handleNext}
                buttonText={getButtonText(activeStep)}
                handleSkip={value.handleCompleteWelcome}
              />
            </motion.div>
          )}

          {!isWelcomeScreen && !isLanguageScreen && (
            <Swiper
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              onSlideChange={(swiper) => {
                const newIndex = swiper.activeIndex + swiperOffset;
                setActiveStep(newIndex);
              }}
              grabCursor={true}
              effect={"creative"}
              creativeEffect={{
                prev: {
                  shadow: true,
                  translate: [0, 0, -400],
                },
                next: {
                  translate: ["100%", 0, 0],
                },
              }}
              modules={[EffectCreative]}
            >
              {translatedScreens.map((screen, index) => {
                if (index > 1) {
                  return (
                    <SwiperSlide
                      className={styles.swiperSlide}
                      key={screen.key}
                    >
                      <WalkthroughScreen
                        title={screen.title}
                        text={screen.text}
                        variant="walkthrough"
                        skipText={t(Constants.translationKeys.skip)}
                        steps={screens.length - 2}
                        activeStep={index - 2}
                        handleNextStep={handleNext}
                        buttonText={getButtonText(activeStep)}
                        image={screen.image}
                        handleSkip={value.handleCompleteWelcome}
                      />
                    </SwiperSlide>
                  );
                }
              })}
            </Swiper>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
};

export default WelcomeWalkthrough;
